import { apiClient } from '../config/api-config';
import type { SessionInfoResponse } from '../schemas/auth-schemas';

/**
 * Session Manager Service
 * Handles inactivity-based session monitoring and user logout
 * Note: Token refresh is now handled automatically by the API client
 */

interface SessionMonitorConfig {
  inactivityTimeout: number; // Inactivity timeout in milliseconds (default: 5 minutes)
  warningBeforeTimeout: number; // Show warning before timeout (ms)
}

class SessionManager {
  private inactivityTimer: ReturnType<typeof setTimeout> | null = null;
  private warningTimer: ReturnType<typeof setTimeout> | null = null;
  private config: SessionMonitorConfig;
  private lastActivityTime: number = Date.now();
  private onInactivityWarning?: (() => void) | undefined;
  private onInactivityLogout?: (() => void) | undefined;
  private activityListeners: Array<() => void> = [];

  constructor(config: Partial<SessionMonitorConfig> = {}) {
    this.config = {
      inactivityTimeout: 5 * 60 * 1000, // 5 minutes default
      warningBeforeTimeout: 30 * 1000, // 30 seconds warning
      ...config,
    };
  }

  /**
   * Start inactivity monitoring
   */
  startMonitoring(): void {
    if (this.inactivityTimer || this.warningTimer) {
      this.stopMonitoring();
    }

    this.lastActivityTime = Date.now();
    this.setupActivityListeners();
    this.resetInactivityTimer();
  }

  /**
   * Stop inactivity monitoring
   */
  stopMonitoring(): void {
    this.clearTimers();
    this.removeActivityListeners();
  }

  /**
   * Clear all timers
   */
  private clearTimers(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
    if (this.warningTimer) {
      clearTimeout(this.warningTimer);
      this.warningTimer = null;
    }
  }

  /**
   * Setup activity listeners for user interactions
   */
  private setupActivityListeners(): void {
    const activityEvents = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
      'click',
    ];

    const handleActivity = () => {
      this.updateActivity();
    };

    activityEvents.forEach((event) => {
      window.addEventListener(event, handleActivity, { passive: true });
      this.activityListeners.push(() => {
        window.removeEventListener(event, handleActivity);
      });
    });
  }

  /**
   * Remove activity listeners
   */
  private removeActivityListeners(): void {
    this.activityListeners.forEach((cleanup) => cleanup());
    this.activityListeners = [];
  }

  /**
   * Update last activity time and reset timers
   */
  private updateActivity(): void {
    const now = Date.now();
    const timeSinceLastActivity = now - this.lastActivityTime;

    // Only reset if there's been significant time since last activity (debounce)
    if (timeSinceLastActivity > 1000) {
      // 1 second debounce
      this.lastActivityTime = now;
      this.resetInactivityTimer();
    }
  }

  /**
   * Reset inactivity timer
   */
  private resetInactivityTimer(): void {
    this.clearTimers();

    const warningTime =
      this.config.inactivityTimeout - this.config.warningBeforeTimeout;

    // Set warning timer
    this.warningTimer = setTimeout(() => {
      this.handleInactivityWarning();
    }, warningTime);

    // Set logout timer
    this.inactivityTimer = setTimeout(() => {
      this.handleInactivityLogout();
    }, this.config.inactivityTimeout);
  }

  /**
   * Handle inactivity warning
   */
  private handleInactivityWarning(): void {
    if (this.onInactivityWarning) {
      this.onInactivityWarning();
    }
  }

  /**
   * Handle inactivity logout
   */
  private handleInactivityLogout(): void {
    this.stopMonitoring();
    if (this.onInactivityLogout) {
      this.onInactivityLogout();
    }
  }

  /**
   * User extended session (reset inactivity timer)
   */
  extendSession(): void {
    this.lastActivityTime = Date.now();
    this.resetInactivityTimer();
  }

  /**
   * Get time until inactivity timeout
   */
  getTimeUntilTimeout(): number {
    const elapsed = Date.now() - this.lastActivityTime;
    const remaining = this.config.inactivityTimeout - elapsed;
    return Math.max(0, remaining);
  }

  /**
   * Check if user is currently inactive
   */
  isInactive(): boolean {
    return this.getTimeUntilTimeout() === 0;
  }

  /**
   * Force session validation (optional - for manual checks)
   * Note: This uses direct apiClient call since session-manager is not a React component
   */
  async validateSession(): Promise<SessionInfoResponse | null> {
    try {
      // Cookies are sent automatically — no payload needed
      const response = await apiClient.axios.post<SessionInfoResponse>(
        '/auth/validate-session'
      );

      if (response) {
        return response.data;
      } else {
        return null;
      }
    } catch {
      return null;
    }
  }

  /**
   * Set event handlers
   */
  setEventHandlers(handlers: {
    onInactivityWarning?: () => void;
    onInactivityLogout?: () => void;
  }): void {
    this.onInactivityWarning = handlers.onInactivityWarning;
    this.onInactivityLogout = handlers.onInactivityLogout;
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<SessionMonitorConfig>): void {
    this.config = { ...this.config, ...newConfig };

    // Restart monitoring if it was running
    if (this.inactivityTimer || this.warningTimer) {
      this.stopMonitoring();
      this.startMonitoring();
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): SessionMonitorConfig {
    return { ...this.config };
  }

  /**
   * Check if monitoring is active
   */
  isMonitoring(): boolean {
    return this.inactivityTimer !== null || this.warningTimer !== null;
  }

  /**
   * Get last activity time
   */
  getLastActivityTime(): number {
    return this.lastActivityTime;
  }
}

// Create singleton instance with default 5-minute inactivity timeout
export const sessionManager = new SessionManager({
  inactivityTimeout: 5 * 60 * 1000, // 5 minutes
  warningBeforeTimeout: 30 * 1000, // 30 seconds warning
});

// Export class for custom instances
export { SessionManager };

// Export types
export type { SessionMonitorConfig };

export default sessionManager;
